import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';

export interface Branch {
    scope: 'project' | 'tenant';
    scopeId: string;
    name: string;
    headCommitId: string;
    createdAt: Date;
    updatedAt: Date;
    updatedBy: string;
}

export interface BranchListResponse {
    branches: Branch[];
    pagination: {
        total: number;
        limit: number;
        offset: number;
        hasMore: boolean;
    };
}

export interface ListBranchesParams {
    projectId: string;
    branchFilter?: string;
    limit?: number;
    offset?: number;
}

export interface CreateBranchParams {
    projectId: string;
    name: string;
    fromCommitId: string;
}

declare var window: Window & { environments: Record<string, string> };

@Injectable({ providedIn: 'root' })
export class BranchService {
    private http = inject(HttpClient);
    private baseUrl = window.environments.ALPHA_UI_DELTA_SERVICE_URL;

    listBranches(params: ListBranchesParams): Observable<BranchListResponse> {
        return this.http.post<BranchListResponse>(`${this.baseUrl}/change-tracking/branches/list`, {
            projectId: params.projectId,
            branchFilter: params.branchFilter,
            limit: params.limit ?? 20,
            offset: params.offset ?? 0,
        });
    }

    /** Get current branch from URL query params */
    getCurrentBranch(): string {
        const params = new URLSearchParams(window.location.search);
        return params.get('branch') || 'main';
    }

    /** Create a new branch */
    createBranch(params: CreateBranchParams): Observable<Branch> {
        return this.http.post<Branch>(`${this.baseUrl}/change-tracking/branches/create`, params);
    }

    /**
     * Validate if a branch exists for a project
     * @param projectId - Project UUID
     * @param branchName - Branch name to validate
     * @returns Observable<boolean> - true if branch exists, false if not found
     */
    validateBranch(projectId: string, branchName: string): Observable<boolean> {
        return this.http
            .post<Branch>(`${this.baseUrl}/change-tracking/branches/get`, { projectId, name: branchName })
            .pipe(
                map(() => true),
                catchError((error) => {
                    if (error?.status === 404 || error?.statusCode === 404) {
                        return of(false);
                    }
                    console.error('Error validating branch:', error);
                    return of(false);
                }),
            );
    }
}
